// A child's recovery shares as its parent sees them (impire-hq
// 02-DESIGN/02-secrets/03-escrow.md § what the console shows; § the verbs:
// identity.escrow.child.<slug>.status, .collect, .present, answered only where
// the caller's workspace is that child's parent). On Workspaces, the row's
// Recovery share column. On the child's page, the *Zero-trust* tab
// (01-the-console.md § the tabs: a child's row opened, on Workspaces; §
// Settings: the Zero-trust tab, its last paragraph): Settings' Zero-trust
// words for the child — what the shares are, the set and its id, the three
// shares — with *Collect* and *Present* for the parent's share and no *New
// shares* (spec 061). Collect's one-time share and Present each stand in the
// tab's place (§ conventions: a form). Nothing here shows a share after the
// moment it is collected, and nothing shows a key, ever: the collected share
// lives in the page's memory while its entry is current, never in the
// history entry. The words are recovery.ts's and zero-trust.ts's.

import { useState } from "react";

import { isRefusal } from "../../platform/bus.ts";
import { NotAllowed, Notice, Pending, Pill, useLoad, when } from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { EscrowAction, EscrowCode, escrowChildSubject, Slot, type EscrowStatus } from "../../wire/escrow.ts";
import { SERVICE } from "../../wire/identity.ts";
import {
  Ceremony,
  OneTimeShare,
  PresentAct,
  PresentForm,
  Shares,
  ShareSet,
  useShares,
  zeroTrustFormDrawn,
  type ShareAct,
  type ZeroTrustState,
} from "../settings/shares.tsx";
import { explainer, PRESENT_FORM, refusalAnswer, type ZeroTrustForm } from "../settings/zero-trust.ts";
import { useSurface } from "../surface.tsx";
import {
  childCollectedLine,
  childOutcome,
  childPresenterWords,
  childPresentLine,
  childRefusal,
  childReplacedLine,
  childSlotWords,
  noChildShares,
  recoveryCell,
  type Between,
  type ChildEscrow,
} from "./recovery.ts";

/** Read is one child's slug and what its status read gave. */
type Read = [string, ChildEscrow];

/**
 * useChildEscrow reads every child's escrow status, one request each; a child
 * whose status answers not-found has no shares the parent holds, and any
 * other refusal is kept to be shown as one.
 */
export function useChildEscrow(slugs: readonly string[], tick: number): Map<string, ChildEscrow> {
  const s = useSurface();
  const readOne = async (slug: string): Promise<Read> => {
    try {
      const status = await s.bus.call<EscrowStatus>(
        SERVICE,
        escrowChildSubject(slug, EscrowAction.status),
        undefined,
      );
      return [slug, { status }];
    } catch (err) {
      return [slug, isRefusal(err, EscrowCode.notFound) ? {} : { err }];
    }
  };
  const [all] = useLoad(
    `escrow-children:${slugs.join(",")}`,
    () => Promise.all(slugs.map(readOne)).then((pairs) => new Map(pairs)),
    tick,
    { keep: true },
  );
  return all.state === "ready" ? all.value : new Map<string, ChildEscrow>();
}

/** RecoveryPill is the row's Recovery column. */
export function RecoveryPill({ e }: { e: ChildEscrow | undefined }) {
  const c = recoveryCell(e, when);
  return (
    <span className="cell">
      <span>
        <Pill tone={c.tone}>{c.text}</Pill>
      </span>
      {c.sub !== undefined && <span className="cell-sub">{c.sub}</span>}
    </span>
  );
}

/** useChildZeroTrust reads a child's shares and acts on the parent's, through the child.<slug> verbs. */
export function useChildZeroTrust(slug: string): ZeroTrustState {
  const s = useSurface();
  return useShares(
    `escrow/child/${slug}`,
    () => s.bus.call<EscrowStatus>(SERVICE, escrowChildSubject(slug, EscrowAction.status), undefined),
    (action: ShareAct, body: unknown) => s.bus.call<unknown>(SERVICE, escrowChildSubject(slug, action), body),
  );
}

/**
 * ChildZeroTrust is the child's *Zero-trust* tab: the parent's part of the
 * child's recovery shares, in Settings' words for the child, with Collect
 * and Present for the parent's share and no New shares.
 */
export function ChildZeroTrust({ z, slug, between }: { z: ZeroTrustState; slug: string; between: Between }) {
  const form = useFormState<ZeroTrustForm>();
  const [outcome, setOutcome] = useState<string | undefined>(undefined);
  const { st } = z;
  const holders = { name: between.child, parent: between.parent };
  const present = () => {
    setOutcome(undefined);
    form.show(PRESENT_FORM);
  };
  if (z.collected !== undefined) {
    return (
      <OneTimeShare
        title={`${between.parent}'s recovery share for ${between.child}`}
        reply={z.collected}
        onClose={z.closeCollected}
      >
        {childCollectedLine(between)}
      </OneTimeShare>
    );
  }
  if (st.state === "ready" && zeroTrustFormDrawn(form.open, false, st.value)) {
    return (
      <PresentForm
        z={z}
        words={{
          title: `Present ${between.parent}'s share`,
          line: childPresentLine(between),
          field: `${between.parent}'s share`,
          slug,
          name: between.child,
          refusal: (e) => refusalAnswer(e) ?? childRefusal(e, between),
        }}
        opens={st.value.ceremony === undefined}
        onClose={form.close}
        onDone={(o) => {
          form.close();
          setOutcome(childOutcome(o, between));
        }}
      />
    );
  }
  // A child with no shares the parent holds answers not-found, as one that is not a child does.
  const none = st.state === "refused" && isRefusal(st.err, EscrowCode.notFound);
  const said = z.err === undefined ? undefined : childRefusal(z.err, between);
  return (
    <div className="stack">
      <div className="card">
        <p>{explainer(holders)}</p>
      </div>
      {st.state === "pending" && <Pending what="Reading" />}
      {st.state === "refused" && !none && <NotAllowed err={st.err} what="Zero-trust" />}
      {(none || (st.state === "ready" && st.value.set === undefined)) && (
        <p className="muted">{noChildShares(between)}</p>
      )}
      {st.state === "ready" && st.value.set !== undefined && (
        <>
          {outcome !== undefined && <Notice kind="ok">{outcome}</Notice>}
          {st.value.ceremony !== undefined && (
            <Ceremony
              status={st.value}
              holders={holders}
              who={(p) => childPresenterWords(p, between, slug)}
            />
          )}
          <ShareSet
            status={st.value}
            name={between.child}
            replaced={childReplacedLine(st.value, between, when)}
          />
          {said !== undefined && (
            <Notice kind="bad" title="Not collected">
              {said}
            </Notice>
          )}
          {z.err !== undefined && said === undefined && <NotAllowed err={z.err} what="Zero-trust" />}
          <Shares
            status={st.value}
            holders={holders}
            mine={Slot.parent}
            words={(r) => childSlotWords(r, between, when)}
            busy={z.busy}
            onCollect={z.collect}
            onPresent={present}
          />
          <div className="card stack">
            <PresentAct busy={z.busy} onPresent={present} line={childPresentLine(between)} />
          </div>
        </>
      )}
    </div>
  );
}
